// #region Imports
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import MuiDrawer from "@mui/material/Drawer";
import { shellBottomBarHeight } from "_shared/_views/shell-bottom-bar";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { SIDE_PANEL_WIDTH } from "_shared/_views/side-panel-width";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExistingFeeScheduleTemplatePicker from "work/fee-schedule-templates/_views/lists/existing-fee-schedule-template-picker";
import type {
  TemplateCategoriesApplied,
  TemplateCategory,
} from "work/fee-schedule-templates/_views/lists/fee-schedule-template-category-picker";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";
// #endregion

// #region Styled Components
const Drawer = styled(MuiDrawer)(() => ({
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    // Ends above the assistant's bar below lg, which stays reachable while the panel is open (#6128).
    height: `calc(100% - 64px - ${shellTopBannersHeight()} - ${shellBottomBarHeight()})`,
    top: `calc(64px + ${shellTopBannersHeight()})`,
    width: SIDE_PANEL_WIDTH,
    boxSizing: 'border-box',
  },
}));
const FeeTemplatePanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition"
})<{ open?: boolean }>(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    width: open ? SIDE_PANEL_WIDTH : 0,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open
        ? theme.transitions.easing.easeOut
        : theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
  },
}));
const FeeTemplatePanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
  minHeight: 0,
}));
const FeeTemplatePanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  height: "100%",
  minHeight: 0,
  padding: theme.spacing(2),
  // Fills the paper rather than restating its width, which would put the padding outside the panel.
  boxSizing: "border-box",
  width: "100%",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
// #endregion

// #region Props/Types
type ExistingFeeScheduleTemplateDrawerProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** What the template being built already holds, so the picker can flag what is already added. */
  existingCategories: readonly TemplateCategory[];
  /** The categories the reader took from a template, and whether they add to or replace these. */
  onCategoriesApplied: (applied: TemplateCategoriesApplied) => void;
};
// #endregion

export default function ExistingFeeScheduleTemplateDrawer(props: Readonly<ExistingFeeScheduleTemplateDrawerProps>) {
  const {
    isOpen,
    onClose,
    existingCategories,
    onCategoriesApplied,
  } = props;

  // #region Render
  return (
    <FeeTemplatePanel open={isOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
      <FeeTemplatePanelContainer>
        <FeeTemplatePanelContent>
          <TitleBar>
            <Typography variant="h5">Select Existing Template</Typography>
            <Tooltip title="Minimize panel">
              <IconButton size="medium" onClick={onClose}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          {/* Mounted while open, so each opening starts at the template list rather than a preview. */}
          {isOpen && (
            <ExistingFeeScheduleTemplatePicker
              existingCategories={existingCategories}
              onApply={onCategoriesApplied}
              onCancel={onClose}
            />
          )}
        </FeeTemplatePanelContent>
      </FeeTemplatePanelContainer>
    </FeeTemplatePanel>
  );
}
// #endregion
